import React, { useState, useEffect } from "react";
import Navbar from "../../components/headerSection/navbar/Navbar";
import { Link, useNavigate } from "react-router-dom";
import FormInput from "../../components/formInput/FormInput";
import { useTranslation } from "react-i18next";
import registerService from "../../services/registerService";

const REGISTER_ACCESS_PASSWORD = "schalke2liga";

function Register() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [isUnlocked, setIsUnlocked] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("registeruser_access") === REGISTER_ACCESS_PASSWORD;
  });
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [inputValue, setInputValue] = useState({
    email: "",
    firstName: "",
    lastName: "",
    company: "",
    password: "",
    password2: "",
    language: i18n.language,
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    setInputValue((prevInputValue) => ({
      ...prevInputValue,
      language: i18n.language,
    }));
  }, [i18n.language]);

  useEffect(() => {
    if (isUnlocked) {
      sessionStorage.setItem("registeruser_access", REGISTER_ACCESS_PASSWORD);
    }
  }, [isUnlocked]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();

    if (passwordInput === REGISTER_ACCESS_PASSWORD) {
      sessionStorage.setItem("registeruser_access", REGISTER_ACCESS_PASSWORD);
      setIsUnlocked(true);
      setPasswordError("");
      return;
    }

    setPasswordError("Falsches Passwort");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputValue((prevInputValue) => ({
      ...prevInputValue,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = {};
    const { email, firstName, lastName, company, password, password2 } =
      inputValue;
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,20}$/;

    if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = t("register.emailInvalid");
      return { valid: false, errors };
    }

    if (!firstName) {
      errors.firstName = t("register.firstNameRequired");
      return { valid: false, errors };
    }

    if (!lastName) {
      errors.lastName = t("register.lastNameRequired");
      return { valid: false, errors };
    }

    if (!company) {
      errors.company = t("register.companyRequired");
      return { valid: false, errors };
    }

    if (!passwordRegex.test(password)) {
      errors.password = t("register.passwordInvalid");
      return { valid: false, errors };
    }

    if (password !== password2) {
      errors.password2 = t("register.confirmPasswordMismatch");
      return { valid: false, errors };
    }

    return { valid: true, errors: {} };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateForm();
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    try {
      const response = await registerService.registerUser(inputValue);
      const { headers, status } = response;

      if (status === 201) {
        setSuccessMessage(t("register.registrationSuccess"));
        setInputValue({
          email: "",
          firstName: "",
          lastName: "",
          company: "",
          password: "",
          password2: "",
          language: i18n.language,
        });
        setErrors({});
      } else if (status === 400) {
        setErrors({ api: t("register.error.userAlreadyExists") });
      } else {
        setErrors({ api: headers["message"] || "Unknown error" });
      }
    } catch (error) {
      setErrors({ api: error.message });
      console.error("Registration error:", error.message);
    }
  };

  if (!isUnlocked) {
    return (
      <div className="poppins">
        <Navbar />

        <div className="w-full mt-10 md:mb-20 mb-10 md:pt-0 pt-20">
          <div className="project-container w-full h-full px-4 flex justify-center items-center">
            <div className="w-full max-w-md rounded-[20px] bg-white/80 p-8 shadow-xl border border-[#E5E7EB]">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#F2EAFE] text-[#4D47C3] text-2xl">
                🔒
              </div>
              <h2 className="text-center text-2xl font-semibold text-[#1F2937]">
                Zugang geschützt
              </h2>
              <p className="mt-3 text-center text-sm text-[#4B5563]">
                Bitte gib das Passwort ein, um die Registrierung zu öffnen.
              </p>

              <form onSubmit={handlePasswordSubmit} className="mt-6">
                <input
                  type="password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="Passwort"
                  className="w-full rounded-[9px] border border-[#D1D5DB] bg-[#F9FAFB] px-4 py-3 text-base text-[#111827] outline-none focus:border-[#4D47C3]"
                />
                {passwordError && (
                  <p className="mt-3 text-sm text-red-600">{passwordError}</p>
                )}
                <button
                  type="submit"
                  className="mt-5 w-full rounded-[9px] bg-[#8B5CF6] py-3 text-base font-semibold text-white shadow-md transition hover:bg-[#7C3AED]"
                >
                  Weiter
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="poppins">
      <Navbar />

      <div className="w-full mt-10 md:mb-20 mb-10 md:pt-0 pt-20">
        <div className="project-container w-full h-full px-4">
          <div className="md:flex justify-center items-center lg:flex-row flex-col">
            <div className="w-full lg:w-8/12 -mt-[100px]">
              <h2 className="text-3xl md:text-4xl mb-8 lg:text-[50px] font-semibold lg:text-start text-center">
                {t("register.title")}
              </h2>
              <h4 className="text-2xl lg:text-[35px] font-medium mb-8 md:mb-10 lg:mb-16 lg:text-start text-center">
                {t("register.account")}
              </h4>
              <p className="text-black mb-2 lg:text-start text-center text-sm md:text-base">
                {t("haveAccount")}
              </p>
              <p className="lg:text-start text-center text-sm md:text-base">
                {t("youCan")}{" "}
                <Link to="/login" className="text-[#4D47C3] font-semibold">
                  {t("loginHere")}
                </Link>
              </p>
            </div>

            <div className="w-full lg:w-4/12 md:mt-0 mt-10">
              <h2 className="text-3xl font-medium mb-6 lg:text-start text-center">
                {t("register.title")}
              </h2>

              <form onSubmit={handleSubmit}>
                <FormInput
                  type="email"
                  placeholder={t("register.email")}
                  value={inputValue.email}
                  onChange={handleInputChange}
                  className="w-full py-5 px-5 bg-inputBgColor rounded-[9px] focus:outline-none mt-4 placeholder:text-[15px] placeholder:text-[#A7A3FF] placeholder:font-normal"
                  name="email"
                  error={errors.email}
                />
                <FormInput
                  type="text"
                  placeholder={t("register.firstName")}
                  value={inputValue.firstName}
                  onChange={handleInputChange}
                  className="w-full py-5 px-5 bg-inputBgColor rounded-[9px] focus:outline-none mt-4 placeholder:text-[15px] placeholder:text-[#A7A3FF] placeholder:font-normal"
                  name="firstName"
                  error={errors.firstName}
                />
                <FormInput
                  type="text"
                  placeholder={t("register.lastName")}
                  value={inputValue.lastName}
                  onChange={handleInputChange}
                  className="w-full py-5 px-5 bg-inputBgColor rounded-[9px] focus:outline-none mt-4 placeholder:text-[15px] placeholder:text-[#A7A3FF] placeholder:font-normal"
                  name="lastName"
                  error={errors.lastName}
                />
                <FormInput
                  type="text"
                  placeholder={t("register.company")}
                  value={inputValue.company}
                  onChange={handleInputChange}
                  className="w-full py-5 px-5 bg-inputBgColor rounded-[9px] focus:outline-none mt-4 placeholder:text-[15px] placeholder:text-[#A7A3FF] placeholder:font-normal"
                  name="company"
                  error={errors.company}
                />
                <FormInput
                  type="password"
                  placeholder={t("register.password")}
                  value={inputValue.password}
                  onChange={handleInputChange}
                  className="w-full py-5 px-5 bg-inputBgColor rounded-[9px] focus:outline-none mt-4 placeholder:text-[15px] placeholder:text-[#A7A3FF] placeholder:font-normal"
                  name="password"
                  error={errors.password}
                />
                <FormInput
                  type="password"
                  placeholder={t("register.confirmPassword")}
                  value={inputValue.password2}
                  onChange={handleInputChange}
                  className="w-full py-5 px-5 bg-inputBgColor rounded-[9px] focus:outline-none mt-4 placeholder:text-[15px] placeholder:text-[#A7A3FF] placeholder:font-normal"
                  name="password2"
                  error={errors.password2}
                />
                <div className="mt-4">
                  <label
                    htmlFor="language"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("register.language")}
                  </label>
                  <select
                    name="language"
                    value={inputValue.language}
                    onChange={handleInputChange}
                    className="w-full py-5 px-5 bg-inputBgColor rounded-[9px] focus:outline-none mt-2"
                  >
                    <option value="en">English</option>
                    <option value="de">Deutsch</option>
                  </select>
                  <p className="text-sm text-gray-500 mt-2">
                    {t("register.languageInfo")}
                  </p>
                </div>
                {errors.api && <p className="error">{errors.api}</p>}
                <button
                  type="submit"
                  className="w-full bg-btnBgColor text-white py-5 rounded-[9px] focus:outline-none focus:ring mt-6 shadow-logRegBtnShadow"
                >
                  {t("register.registerButton")}
                </button>
              </form>

              {successMessage && (
                <div className="mt-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <span className="text-base font-medium">
                      {successMessage}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-green-600">
                    Bitte prüfen Sie Ihren E-Mail-Posteingang und bestätigen Sie
                    Ihre E-Mail-Adresse.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
